import {
    AppwriteException,
    Client,
    type Payload,
    applyDefaults,
} from '../client';
import type { Models } from '../models';

import { ExecutionMethod } from '../enums/execution-method';
export class Functions {
    client: Client;

    constructor(client: Client) {
        this.client = client;
    }

    /**
     * Get a list of all the current user function execution logs. You can use the query params to filter your results.
     *
     * @param {string} params.functionId - Function ID.
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: trigger, status, responseStatusCode, duration, requestMethod, requestPath, deploymentId
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ExecutionList>}
     */
    listExecutions(params: {
        functionId: string;
        queries?: string[];
        total?: boolean;
    }): Promise<Models.ExecutionList>;
    /**
     * Get a list of all the current user function execution logs. You can use the query params to filter your results.
     *
     * @param {string} functionId - Function ID.
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: trigger, status, responseStatusCode, duration, requestMethod, requestPath, deploymentId
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ExecutionList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listExecutions(
        functionId: string,
        queries?: string[],
        total?: boolean,
    ): Promise<Models.ExecutionList>;
    listExecutions(
        paramsOrFirst:
            | { functionId: string; queries?: string[]; total?: boolean }
            | string,
        ...rest: [string[]?, boolean?]
    ): Promise<Models.ExecutionList> {
        let params: { functionId: string; queries?: string[]; total?: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                functionId: string;
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                functionId: paramsOrFirst as string,
                queries: rest[0] as string[],
                total: rest[1] as boolean,
            };
        }

        const functionId = params.functionId;
        const queries = params.queries;
        const total = params.total;

        if (typeof functionId === 'undefined' || functionId === '') {
            throw new AppwriteException(
                'Missing required parameter: "functionId"',
            );
        }
        const apiPath = '/functions/{functionId}/executions'.replace(
            '{functionId}',
            encodeURIComponent(String(functionId)),
        );
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'executionList',
            models: {
                executionList: {
                    defaults: {
                        total: 0,
                        executions: [],
                    },
                    properties: {
                        executions: {
                            items: 'execution',
                        },
                    },
                },
                execution: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        resourceId: '',
                        resourceType: 'functions',
                        deploymentId: '',
                        requestMethod: '',
                        requestPath: '',
                        requestHeaders: [],
                        responseStatusCode: 0,
                        responseBody: '',
                        responseHeaders: [],
                        logs: '',
                        errors: '',
                        duration: 0,
                    },
                    properties: {
                        requestHeaders: {
                            items: 'headers',
                        },
                        responseHeaders: {
                            items: 'headers',
                        },
                    },
                },
                headers: {
                    defaults: {
                        name: '',
                        value: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Trigger a function execution. The returned object will return you the current execution status. You can ping the `Get Execution` endpoint to get updates on the current execution status. Once this endpoint is called, your function execution process will start asynchronously.
     *
     * @param {string} params.functionId - Function ID.
     * @param {string} params.body - HTTP body of execution. Default value is empty string.
     * @param {boolean} params.async - Execute code in the background. Default value is false.
     * @param {string} params.xpath - HTTP path of execution. Path can include query params. Default value is /
     * @param {ExecutionMethod} params.method - HTTP method of execution. Default value is POST.
     * @param {object} params.headers - HTTP headers of execution. Defaults to empty.
     * @param {string | null} params.scheduledAt - Scheduled execution time in [ISO 8601](https://www.iso.org/iso-8601-date-and-time-format.html) format. DateTime value must be in future with precision in minutes.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Execution>}
     */
    createExecution(params: {
        functionId: string;
        body?: string;
        async?: boolean;
        xpath?: string;
        method?: ExecutionMethod;
        headers?: object;
        scheduledAt?: string | null;
    }): Promise<Models.Execution>;
    /**
     * Trigger a function execution. The returned object will return you the current execution status. You can ping the `Get Execution` endpoint to get updates on the current execution status. Once this endpoint is called, your function execution process will start asynchronously.
     *
     * @param {string} functionId - Function ID.
     * @param {string} body - HTTP body of execution. Default value is empty string.
     * @param {boolean} async - Execute code in the background. Default value is false.
     * @param {string} xpath - HTTP path of execution. Path can include query params. Default value is /
     * @param {ExecutionMethod} method - HTTP method of execution. Default value is POST.
     * @param {object} headers - HTTP headers of execution. Defaults to empty.
     * @param {string | null} scheduledAt - Scheduled execution time in [ISO 8601](https://www.iso.org/iso-8601-date-and-time-format.html) format. DateTime value must be in future with precision in minutes.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Execution>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createExecution(
        functionId: string,
        body?: string,
        async?: boolean,
        xpath?: string,
        method?: ExecutionMethod,
        headers?: object,
        scheduledAt?: string | null,
    ): Promise<Models.Execution>;
    createExecution(
        paramsOrFirst:
            | {
                  functionId: string;
                  body?: string;
                  async?: boolean;
                  xpath?: string;
                  method?: ExecutionMethod;
                  headers?: object;
                  scheduledAt?: string | null;
              }
            | string,
        ...rest: [
            string?,
            boolean?,
            string?,
            ExecutionMethod?,
            object?,
            (string | null)?,
        ]
    ): Promise<Models.Execution> {
        let params: {
            functionId: string;
            body?: string;
            async?: boolean;
            xpath?: string;
            method?: ExecutionMethod;
            headers?: object;
            scheduledAt?: string | null;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                functionId: string;
                body?: string;
                async?: boolean;
                xpath?: string;
                method?: ExecutionMethod;
                headers?: object;
                scheduledAt?: string | null;
            };
        } else {
            params = {
                functionId: paramsOrFirst as string,
                body: rest[0] as string,
                async: rest[1] as boolean,
                xpath: rest[2] as string,
                method: rest[3] as ExecutionMethod,
                headers: rest[4] as object,
                scheduledAt: rest[5] as string | null,
            };
        }

        const functionId = params.functionId;
        const body = params.body;
        const async = params.async;
        const xpath = params.xpath;
        const method = params.method;
        const headers = params.headers;
        const scheduledAt = params.scheduledAt;

        if (typeof functionId === 'undefined' || functionId === '') {
            throw new AppwriteException(
                'Missing required parameter: "functionId"',
            );
        }
        const apiPath = '/functions/{functionId}/executions'.replace(
            '{functionId}',
            encodeURIComponent(String(functionId)),
        );
        const apiPayload: Payload = {};
        if (typeof body !== 'undefined') {
            apiPayload['body'] = body;
        }
        if (typeof async !== 'undefined') {
            apiPayload['async'] = async;
        }
        if (typeof xpath !== 'undefined') {
            apiPayload['path'] = xpath;
        }
        if (typeof method !== 'undefined') {
            apiPayload['method'] = method;
        }
        if (typeof headers !== 'undefined') {
            apiPayload['headers'] = headers;
        }
        if (typeof scheduledAt !== 'undefined') {
            apiPayload['scheduledAt'] = scheduledAt;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'execution',
            models: {
                execution: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        resourceId: '',
                        resourceType: 'functions',
                        deploymentId: '',
                        requestMethod: '',
                        requestPath: '',
                        requestHeaders: [],
                        responseStatusCode: 0,
                        responseBody: '',
                        responseHeaders: [],
                        logs: '',
                        errors: '',
                        duration: 0,
                    },
                    properties: {
                        requestHeaders: {
                            items: 'headers',
                        },
                        responseHeaders: {
                            items: 'headers',
                        },
                    },
                },
                headers: {
                    defaults: {
                        name: '',
                        value: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a function execution log by its unique ID.
     *
     * @param {string} params.functionId - Function ID.
     * @param {string} params.executionId - Execution ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Execution>}
     */
    getExecution(params: {
        functionId: string;
        executionId: string;
    }): Promise<Models.Execution>;
    /**
     * Get a function execution log by its unique ID.
     *
     * @param {string} functionId - Function ID.
     * @param {string} executionId - Execution ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Execution>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getExecution(
        functionId: string,
        executionId: string,
    ): Promise<Models.Execution>;
    getExecution(
        paramsOrFirst: { functionId: string; executionId: string } | string,
        ...rest: [string?]
    ): Promise<Models.Execution> {
        let params: { functionId: string; executionId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                functionId: string;
                executionId: string;
            };
        } else {
            params = {
                functionId: paramsOrFirst as string,
                executionId: rest[0] as string,
            };
        }

        const functionId = params.functionId;
        const executionId = params.executionId;

        if (typeof functionId === 'undefined' || functionId === '') {
            throw new AppwriteException(
                'Missing required parameter: "functionId"',
            );
        }
        if (typeof executionId === 'undefined' || executionId === '') {
            throw new AppwriteException(
                'Missing required parameter: "executionId"',
            );
        }
        const apiPath = '/functions/{functionId}/executions/{executionId}'
            .replace('{functionId}', encodeURIComponent(String(functionId)))
            .replace('{executionId}', encodeURIComponent(String(executionId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'execution',
            models: {
                execution: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        resourceId: '',
                        resourceType: 'functions',
                        deploymentId: '',
                        requestMethod: '',
                        requestPath: '',
                        requestHeaders: [],
                        responseStatusCode: 0,
                        responseBody: '',
                        responseHeaders: [],
                        logs: '',
                        errors: '',
                        duration: 0,
                    },
                    properties: {
                        requestHeaders: {
                            items: 'headers',
                        },
                        responseHeaders: {
                            items: 'headers',
                        },
                    },
                },
                headers: {
                    defaults: {
                        name: '',
                        value: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }
}
